import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import './Breadcrumbs.css';

/**
 * Breadcrumbs component with Schema.org markup for SEO
 * Helps users navigate and improves Google search appearance
 */
const Breadcrumbs = () => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const currentLang = i18n.language || 'es';
  
  // Parse the current path
  const pathnames = location.pathname.split('/').filter(x => x);
  
  // Remove language prefix if it exists
  const cleanPathnames = (pathnames[0] != null && pathnames[0].length === 2) ? pathnames.slice(1) : pathnames;
  
  // Don't show breadcrumbs on homepage or /itinerario
  if (cleanPathnames.length === 0 || cleanPathnames[0] === 'itinerario') {
    return null;
  }
  
  const DOMAIN = 'https://planiaro.com';
  
  // Labels for common routes (multilingual)
  const routeLabels = {
    es: {
      'blog': 'Blog',
      'planificador-ia': 'Planificador IA',
      'contact': 'Contacto',
      'privacy-policy': 'Política de Privacidad',
      'dashboard': 'Mi Panel',
      'login': 'Iniciar Sesión',
      'register': 'Registrarse'
    },
    en: {
      'blog': 'Blog',
      'planificador-ia': 'AI Planner',
      'contact': 'Contact',
      'privacy-policy': 'Privacy Policy',
      'dashboard': 'Dashboard',
      'login': 'Login',
      'register': 'Sign Up'
    },
    de: {
      'blog': 'Blog',
      'planificador-ia': 'KI-Planer',
      'contact': 'Kontakt',
      'privacy-policy': 'Datenschutz',
      'dashboard': 'Dashboard',
      'login': 'Anmelden',
      'register': 'Registrieren'
    },
    fr: {
      'blog': 'Blog',
      'planificador-ia': 'Planificateur IA',
      'contact': 'Contact',
      'privacy-policy': 'Politique de confidentialité',
      'dashboard': 'Tableau de bord',
      'login': 'Connexion',
      'register': 'S\'inscrire'
    },
    it: {
      'blog': 'Blog',
      'planificador-ia': 'Pianificatore IA',
      'contact': 'Contatto',
      'privacy-policy': 'Privacy Policy',
      'dashboard': 'Dashboard',
      'login': 'Accedi',
      'register': 'Registrati'
    },
    pt: {
      'blog': 'Blog',
      'planificador-ia': 'Planejador IA',
      'contact': 'Contato',
      'privacy-policy': 'Política de Privacidade',
      'dashboard': 'Painel',
      'login': 'Entrar',
      'register': 'Cadastrar'
    }
  };
  
  const labels = routeLabels[currentLang] || routeLabels.es;
  
  // Build breadcrumb items
  const breadcrumbItems = [
    {
      name: 'Home',
      url: `${DOMAIN}/${currentLang}`,
      position: 1
    }
  ];
  
  let currentPath = `/${currentLang}`;
  
  cleanPathnames.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const label = labels[segment] || decodeURIComponent(segment).replace(/-/g, ' ');
    
    breadcrumbItems.push({
      name: label.charAt(0).toUpperCase() + label.slice(1),
      url: `${DOMAIN}${currentPath}`,
      position: index + 2
    });
  });
  
  // Schema.org BreadcrumbList structured data
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': breadcrumbItems.map(item => ({
      '@type': 'ListItem',
      'position': item.position,
      'name': item.name,
      'item': item.url
    }))
  };
  
  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
      
      <nav className="breadcrumbs" aria-label="Breadcrumb">
        <ol className="breadcrumbs-list">
          {breadcrumbItems.map((item, index) => {
            const isLast = index === breadcrumbItems.length - 1;
            
            return (
              <li key={index} className="breadcrumb-item">
                {!isLast ? (
                  <>
                    <Link to={item.url.replace(DOMAIN, '')} className="breadcrumb-link">
                      {item.name}
                    </Link>
                    <span className="breadcrumb-separator" aria-hidden="true"> / </span>
                  </>
                ) : (
                  <span className="breadcrumb-current" aria-current="page">
                    {item.name}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
};

export default Breadcrumbs;
